import Script from 'next/script'
import '@/styles/reset.css'
import 'nprogress/nprogress.css'
import '@/styles/globals.css'
import { useState, useEffect } from 'react'
import Router, { useRouter } from 'next/router'
import NProgress from 'nprogress'
import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import { motion, AnimatePresence } from 'framer-motion'
import { Observable } from 'rxjs'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import PageTransition, { useAsPathWithoutHash } from '@madeinhaus/nextjs-page-transition'
import '@madeinhaus/nextjs-page-transition/dist/index.css'

import CookieConsentRibbon from '@/components/CookieConsentRibbon'
import styles from '@/components/FooterContainer.module.css'
//import { pageView } from "@/utils/Analytics";

import '@react-pdf-viewer/core/lib/styles/index.css'
import '@react-pdf-viewer/default-layout/lib/styles/index.css'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/api/query-client'
import AmplitudeContextProvider from '@/contexts/AmplitudeContext'
import TagManager from 'react-gtm-module'
import React from 'react'

NProgress.configure({ showSpinner: true })

// Bind the NProgress events to the router events.
Router.events.on('routeChangeStart', () => {
  NProgress.start()
})
Router.events.on('routeChangeComplete', () => {
  NProgress.done()
})
Router.events.on('routeChangeError', () => {
  NProgress.done()
})

// Ensure Observable is available globally
if (typeof globalThis !== 'undefined') {
  globalThis.Observable = Observable
} else if (typeof window !== 'undefined') {
  window.Observable = Observable
} else if (typeof global !== 'undefined') {
  global.Observable = Observable
}

export default function App({ Component, pageProps }) {
  const router = useRouter()
  const key = useAsPathWithoutHash()
  const [transitionState, setTransitionState] = useState(null)
  const [isLogoVisible, setIsLogoVisible] = useState(true)

  function useBannerRefresh() {
    const router = useRouter()
    React.useEffect(() => {
      const refresh = () => {
        if (window.AF) {
          window.AF('banners', 'hideBanner')
          window.AF('banners', 'showBanner')
        }
      }
      router.events.on('routeChangeComplete', refresh)
      return () => router.events.off('routeChangeComplete', refresh)
    }, [router.events])
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      TagManager.initialize({ gtmId: 'GTM-5PPSJF5G' })
    }

    const user_id = Cookies.get('user_id')
    if (!user_id) {
      const newUserId = uuidv4()
      Cookies.set('user_id', newUserId, { expires: 365 })
    }
    /*
                        const handleRouteChange = (url) => {
                          //pageView(url);
                        };
                        router.events.on("routeChangeComplete", handleRouteChange);
                        return () => {
                          router.events.off("routeChangeComplete", handleRouteChange);
                        };
        
                     */

    const startProgress = async () => {
      setIsLogoVisible(true)
      setTransitionState('initial')
    }

    const stopProgress = async () => {
      return new Promise((resolve) => {
        setIsLogoVisible(false)

        setTimeout(() => {
          setTransitionState('complete')
          resolve()
        }, 200)
      }).then(() => {
        setTimeout(() => {
          setTransitionState(null)
        }, 800)
      })
    }

    Router.events.on('routeChangeStart', startProgress)
    Router.events.on('routeChangeComplete', stopProgress)
    Router.events.on('routeChangeError', stopProgress)

    return () => {
      Router.events.off('routeChangeStart', startProgress)
      Router.events.off('routeChangeComplete', stopProgress)
      Router.events.off('routeChangeError', stopProgress)
    }
  }, [])

  const getAnimation = () => {
    switch (transitionState) {
      case 'initial':
        return { x: '0%', skewX: '0deg' }
      case 'complete':
        return { x: '110%', skewX: '-10deg' }
      default:
        return { x: '-110%', skewX: '-10deg' }
    }
  }
  useBannerRefresh() // <— keeps the banner alive
  return (
    <>
      {/*<AnimatePresence mode='wait'>
        <motion.div key={router.route} initial="pageInitial" animate="pageAnimate" exit="pageExit" variants={{
          pageInitial: {
            opacity: 0,
          },
          pageAnimate: {
            opacity: 1,
          },
          pageExit: {
            opacity: 0,
          },
        }}>*/}
      {process.env.NEXT_PUBLIC_DOMAIN === 'https://clearwatt.co.uk' && (
        <>
          <Script id="hotjar-script" strategy="afterInteractive">
            {`
              (function (c, s, q, u, a, r, e) {
                  c.hj = c.hj || function(){(c.hj.q = c.hj.q || []).push(arguments)};
                  c._hjSettings = { hjid: a };
                  r = s.getElementsByTagName('head')[0];
                  e = s.createElement('script');
                  e.async = true;
                  e.src = q + c._hjSettings.hjid + u;
                  r.appendChild(e);
              })(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', 5315862);
            `}
          </Script>

          <Script
            id="af-loader"
            strategy="beforeInteractive"
            dangerouslySetInnerHTML={{
              __html: `
      !function(t,e,n,s,a,c,i,o,p){
        t.AppsFlyerSdkObject=a;
        t.AF=t.AF||function(){(t.AF.q=t.AF.q||[]).push([Date.now()].concat(Array.prototype.slice.call(arguments)))};
        t.AF.id=t.AF.id||i;
        t.AF.plugins={};
        o=e.createElement(n); p=e.getElementsByTagName(n)[0]; o.async=1;
        o.src="https://websdk.appsflyer.com?"+(c.length>0?"st="+c.split(",").sort().join(",")+"&":"")+(i.length>0?"af_id="+i:"");
        p.parentNode.insertBefore(o,p);
      }(window,document,"script",0,"AF","banners",{banners:{key:"b463880d-9484-4b17-a275-33bf1e651458"}});
      AF('banners','showBanner');
    `,
            }}
          />

          <Script id="hs-script-loader" strategy="afterInteractive" src="//js.hs-scripts.com/49155041.js" async defer />
        </>
      )}

      {transitionState && (
        <AnimatePresence mode="wait">
          <div className="transition-overlay">
            {isLogoVisible && (
              <div className="logo">
                <img className={styles.smallLogoIcon} alt="" src="/smalllogo.svg" />
                <span>CHARGING</span>
              </div>
            )}
            <motion.div
              initial={{ x: '-110%', skewX: '-10deg' }}
              animate={getAnimation()}
              exit={{ x: '-110%', skewX: '10deg' }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="skew-div"
            ></motion.div>
          </div>
        </AnimatePresence>
      )}
      <QueryClientProvider client={queryClient}>
        <AmplitudeContextProvider>
          <ReactQueryDevtools initialIsOpen={false}></ReactQueryDevtools>
          <PageTransition inPhaseDuration={600} outPhaseDuration={70}>
            <Component {...pageProps} key={key} />
          </PageTransition>
        </AmplitudeContextProvider>
      </QueryClientProvider>
      {/*</motion.div>
      </AnimatePresence>*/}
      <CookieConsentRibbon />
    </>
  )
}
